// src/pages/admin/adminPageEditor/hooks/useSectionAutosave.js
import { useCallback, useEffect, useRef } from "react";
import { mergeSectionPatch } from "../utils/editorUtils";

function mergeServerSection(localSection, serverSection) {
  return {
    ...localSection,
    ...serverSection,
    enabled: serverSection?.enabled ?? localSection.enabled,
    type: serverSection?.type ?? localSection.type,
    template: serverSection?.template ?? localSection.template,
    config: { ...(localSection.config || {}), ...(serverSection?.config || {}) },
    style: { ...(localSection.style || {}), ...(serverSection?.style || {}) },
  };
}

export function useSectionAutosave({ pageId, updateSection, setSections, sectionsRef }) {
  const saveTimersRef = useRef({});
  const pendingPatchRef = useRef({}); // { [sectionId]: patch }

  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const scheduleAutosave = useCallback(
    (sectionId) => {
      if (!pageId || !sectionId) return;

      clearTimeout(saveTimersRef.current[sectionId]);
      saveTimersRef.current[sectionId] = setTimeout(async () => {
        const patch = pendingPatchRef.current[sectionId];
        if (!patch) return;

        delete pendingPatchRef.current[sectionId];

        try {
          const res = await updateSection(pageId, sectionId, patch);
          if (res?.section) {
            setSections((prev) =>
              prev.map((s) => (s.id === sectionId ? mergeServerSection(s, res.section) : s))
            );
          }
        } catch (e) {
          console.error("Section autosave failed:", e);
          // keep it queued (merge)
          pendingPatchRef.current[sectionId] = {
            ...(pendingPatchRef.current[sectionId] || {}),
            ...(patch || {}),
            config: { ...(patch?.config || {}), ...(pendingPatchRef.current[sectionId]?.config || {}) },
            style: { ...(patch?.style || {}), ...(pendingPatchRef.current[sectionId]?.style || {}) },
          };
        }
      }, 450);
    },
    [pageId, updateSection, setSections]
  );

  const patchSectionLive = useCallback(
    (sectionId, patch) => {
      const currentSection = sectionsRef.current?.find((s) => s.id === sectionId);
      if (!currentSection) return;

      const safePatch = {
        type: currentSection.type,
        template: currentSection.template,
        enabled: currentSection.enabled ?? true,
        ...patch,
      };

      setSections((prev) =>
        prev.map((s) => (s.id === sectionId ? mergeSectionPatch(s, safePatch) : s))
      );

      const current = pendingPatchRef.current[sectionId] || {};
      pendingPatchRef.current[sectionId] = {
        ...current,
        ...safePatch,
        config: { ...(current.config || {}), ...(safePatch.config || {}) },
        style: { ...(current.style || {}), ...(safePatch.style || {}) },
      };

      scheduleAutosave(sectionId);
    },
    [setSections, scheduleAutosave, sectionsRef]
  );

  const flushSectionAutosave = useCallback(
    async (sectionId) => {
      if (!pageId || !sectionId) return;
      clearTimeout(saveTimersRef.current[sectionId]);

      const patch = pendingPatchRef.current[sectionId];
      if (!patch) return;

      delete pendingPatchRef.current[sectionId];

      try {
        const res = await updateSection(pageId, sectionId, patch);
        if (res?.section) {
          setSections((prev) =>
            prev.map((s) => (s.id === sectionId ? mergeServerSection(s, res.section) : s))
          );
        }
      } catch (e) {
        console.error("Flush section autosave failed:", e);
      }
    },
    [pageId, updateSection, setSections]
  );

  return { patchSectionLive, flushSectionAutosave };
}